/**
 * Conversions between `LinesDiff` (in-memory class instances) and
 * `WireLinesDiff` (plain JSON-friendly shape used on the worker boundary).
 */

import {
    LinesDiff,
    DetailedLineRangeMapping,
    LineRange,
    LineRangeMapping,
    MovedText,
    RangeMapping,
} from '../defaultLinesDiffComputer/rangeMapping.js';
import { Range } from '../defaultLinesDiffComputer/linesSliceCharSequence.js';
import type {
    WasmDetailedLineRangeMapping,
    WasmRangeMapping,
} from '../wasm/types.js';
import type { WireLinesDiff, WireMovedText } from './protocol.js';

export function linesDiffToWire(diff: LinesDiff): WireLinesDiff {
    return {
        changes: diff.changes.map(detailedToWire),
        moves: diff.moves.map(moveToWire),
        hitTimeout: diff.hitTimeout,
    };
}

export function wireToLinesDiff(wire: WireLinesDiff): LinesDiff {
    const changes = wire.changes.map(wireToDetailed);
    const moves = wire.moves.map(wireToMove);
    return new LinesDiff(changes, moves, wire.hitTimeout);
}

function detailedToWire(m: DetailedLineRangeMapping): WasmDetailedLineRangeMapping {
    return {
        original: {
            startLineNumber: m.original.startLineNumber,
            endLineNumberExclusive: m.original.endLineNumberExclusive,
        },
        modified: {
            startLineNumber: m.modified.startLineNumber,
            endLineNumberExclusive: m.modified.endLineNumberExclusive,
        },
        innerChanges: m.innerChanges ? m.innerChanges.map(rangeMappingToWire) : null,
    };
}

function wireToDetailed(m: WasmDetailedLineRangeMapping): DetailedLineRangeMapping {
    const inner = m.innerChanges?.map(wireToRangeMapping);
    return new DetailedLineRangeMapping(
        new LineRange(m.original.startLineNumber, m.original.endLineNumberExclusive),
        new LineRange(m.modified.startLineNumber, m.modified.endLineNumberExclusive),
        inner,
    );
}

function rangeMappingToWire(rm: RangeMapping): WasmRangeMapping {
    return {
        originalStartLine: rm.originalRange.startLineNumber,
        originalStartColumn: rm.originalRange.startColumn,
        originalEndLine: rm.originalRange.endLineNumber,
        originalEndColumn: rm.originalRange.endColumn,
        modifiedStartLine: rm.modifiedRange.startLineNumber,
        modifiedStartColumn: rm.modifiedRange.startColumn,
        modifiedEndLine: rm.modifiedRange.endLineNumber,
        modifiedEndColumn: rm.modifiedRange.endColumn,
    };
}

function wireToRangeMapping(rm: WasmRangeMapping): RangeMapping {
    return new RangeMapping(
        new Range(rm.originalStartLine, rm.originalStartColumn, rm.originalEndLine, rm.originalEndColumn),
        new Range(rm.modifiedStartLine, rm.modifiedStartColumn, rm.modifiedEndLine, rm.modifiedEndColumn),
    );
}

function moveToWire(m: MovedText): WireMovedText {
    return {
        lineRangeMapping: {
            original: {
                startLineNumber: m.lineRangeMapping.original.startLineNumber,
                endLineNumberExclusive: m.lineRangeMapping.original.endLineNumberExclusive,
            },
            modified: {
                startLineNumber: m.lineRangeMapping.modified.startLineNumber,
                endLineNumberExclusive: m.lineRangeMapping.modified.endLineNumberExclusive,
            },
        },
        changes: m.changes.map(detailedToWire),
    };
}

function wireToMove(m: WireMovedText): MovedText {
    const lrm = new LineRangeMapping(
        new LineRange(m.lineRangeMapping.original.startLineNumber, m.lineRangeMapping.original.endLineNumberExclusive),
        new LineRange(m.lineRangeMapping.modified.startLineNumber, m.lineRangeMapping.modified.endLineNumberExclusive),
    );
    return new MovedText(lrm, m.changes.map(wireToDetailed));
}
